import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("sbi_admin_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export const sendChat = (session_id, message) =>
  api.post("/chat", { session_id, message }).then((r) => r.data);

/**
 * Stream a chat response via Server-Sent Events.
 *
 * The server emits ``data: {"chunk": "..."}`` events as LLM tokens arrive,
 * then a ``data: {"parsed": ai_json}`` preview event once the LLM output
 * has been parsed but Amazon enrichment is still running, and finally a
 * single ``data: {"done": true, "payload": {...}}`` event with the fully
 * enriched response. Repeat queries return a single ``data: {"cached":
 * true}`` sentinel immediately followed by a synthetic ``done`` event.
 * On failure a ``data: {"error": "..."}`` event is sent and the stream
 * ends.
 *
 * @param {string|null} sessionId  existing session id (null for first turn)
 * @param {string} message         the user message
 * @param {{onChunk: (text: string) => void, onParsed: (ai: object) => void, onDone: (payload: object) => void, onCached: () => void, onError: (msg: string) => void}} cbs
 * @returns {() => void}  abort function — call to cancel the stream
 */
export const streamChat = (sessionId, message, { onChunk, onParsed, onDone, onCached, onError }) => {
  const ctrl = new AbortController();
  (async () => {
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        onError?.(`Stream HTTP ${resp.status}`);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE events are separated by a blank line. Parse complete events
        // out of the buffer, leaving the trailing partial in place.
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const ev = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = ev.startsWith("data: ") ? ev.slice(6) : ev;
          if (!line) continue;
          let obj;
          try {
            obj = JSON.parse(line);
          } catch {
            continue; // ignore malformed events (heartbeats etc.)
          }
          if (obj.error) {
            onError?.(obj.error);
            return;
          }
          if (obj.done) {
            onDone?.(obj.payload);
            return;
          }
          if (obj.cached) {
            onCached?.();
            continue;
          }
          if (obj.parsed) {
            onParsed?.(obj.parsed);
            continue;
          }
          if (typeof obj.chunk === "string") {
            onChunk?.(obj.chunk);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") onError?.(String(e?.message || e));
    }
  })();
  return () => ctrl.abort();
};

export const getSession = (sessionId) =>
  api.get(`/chat/${sessionId}`).then((r) => r.data);

export const fetchRecentSearches = () =>
  api.get("/searches/recent").then((r) => r.data);

export const fetchPopularSearches = () =>
  api.get("/searches/popular").then((r) => r.data);

export const fetchTodayPicks = (limit = 6) =>
  api.get("/home/today-picks", { params: { limit } }).then((r) => r.data);

export const fetchTrendingQuestions = (limit = 6) =>
  api.get("/home/trending-questions", { params: { limit } }).then((r) => r.data);

export const fetchCategoryWinners = () =>
  api.get("/home/category-winners").then((r) => r.data);

export const fetchHiddenGems = (limit = 4) =>
  api.get("/home/hidden-gems", { params: { limit } }).then((r) => r.data);

export const fetchAvoidTips = (limit = 6) =>
  api.get("/home/avoid", { params: { limit } }).then((r) => r.data);

export const fetchPublicStats = () =>
  api.get("/stats/public").then((r) => r.data);

export const trackAffiliate = (payload) =>
  api.post("/affiliate/click", payload).then((r) => r.data);

export const fetchAffiliateLink = (search_id, cta) =>
  api.get("/affiliate/link", { params: { search_id, cta } }).then((r) => r.data);

export const recordPurchase = (search_id, bought) =>
  api.post(`/recommendations/${search_id}/purchase`, { bought }).then((r) => r.data);

export const fetchAffiliateStats = () =>
  api.get("/admin/affiliate/stats").then((r) => r.data);

export const voteOnRecommendation = (searchId, helpful) =>
  api.post(`/recommendations/${searchId}/vote`, { helpful }).then((r) => r.data);

export const markShared = (searchId) =>
  api.post(`/recommendations/${searchId}/share`).then((r) => r.data);

export const fetchPublicRecommendation = (searchId) =>
  api.get(`/r/${searchId}`).then((r) => r.data);

export const adminLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);

export const fetchAdminStats = () =>
  api.get("/admin/stats").then((r) => r.data);

export const fetchAdminSearches = () =>
  api.get("/admin/searches").then((r) => r.data);

// QA / audit
export const fetchQaSearches = (params = {}) =>
  api.get("/admin/qa/searches", { params }).then((r) => r.data);

export const fetchQaSearch = (id) =>
  api.get(`/admin/qa/searches/${id}`).then((r) => r.data);

export const setAuditRating = (id, rating) =>
  api.post(`/admin/qa/searches/${id}/rating`, { rating }).then((r) => r.data);

export const addAuditNote = (id, note) =>
  api.post(`/admin/qa/searches/${id}/note`, { note }).then((r) => r.data);

export const deleteAuditNote = (id, noteId) =>
  api.delete(`/admin/qa/searches/${id}/note/${noteId}`).then((r) => r.data);

export const fetchReviewQueue = () =>
  api.get("/admin/qa/review-queue").then((r) => r.data);

export const fetchFeedbackAudit = () =>
  api.get("/admin/audit/feedback").then((r) => r.data);

export const bulkSetFeedback = (ids, feedback) =>
  api.post("/admin/qa/bulk-feedback", { ids, feedback }).then((r) => r.data);

export const setAdminFeedback = (id, feedback) =>
  api.post(`/admin/qa/searches/${id}/feedback`, { feedback }).then((r) => r.data);


export const resetDevMetrics = ({ includeAffiliateClicks = true } = {}) =>
  api
    .post("/admin/reset/dev-metrics", {
      confirm: "RESET",
      include_affiliate_clicks: includeAffiliateClicks,
    })
    .then((r) => r.data);
